// Word Changes from Review: after the Author replaces a word in a Block, ask
// where else the Book uses it, and apply the accepted suggestions outside the
// Chapter being edited. Suggestions inside that Chapter never go through the
// server's apply: they land in the Chapter's open draft, like any other edit.

import type {
  WordChangeApplyRequest,
  WordChangeApplyResult,
  WordChangeSuggestions,
  WordChangeSuggestRequest,
} from "@contract";
import { buildPath } from "@contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";

export function useSuggestWordChange(bookId: string) {
  return useMutation({
    mutationFn: (request: WordChangeSuggestRequest) =>
      api<WordChangeSuggestions>(buildPath("/books/:id/word-changes/suggestions", { id: bookId }), {
        method: "POST",
        body: request,
      }),
  });
}

export function useApplyWordChange(bookId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: WordChangeApplyRequest) =>
      api<WordChangeApplyResult>(buildPath("/books/:id/word-changes/apply", { id: bookId }), {
        method: "POST",
        body: request,
      }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: queryKeys.book(bookId) });
      if (result.styleSheet?.version)
        void client.invalidateQueries({ queryKey: queryKeys.styleSheet(bookId) });
      for (const chapter of result.chapters)
        if (chapter.version !== null)
          void client.invalidateQueries({
            queryKey: ["chapterReview", bookId, chapter.chapterId],
          });
    },
  });
}

/** One suggestion's identity inside a result: Block, place, and the word found there. */
export const suggestionKey = (chapterId: string, blockId: string, offset: number) =>
  `${chapterId}:${blockId}:${offset}`;
